import {
	CheckboxVisibility,
	ConstrainMode,
	DetailsListCompat as DetailsList,
	IColumn,
	IDetailsHeaderProps,
	IDetailsList,
	IDetailsListProps,
	IDetailsListStyles,
	IGroup,
	IRenderFunction,
	ISelection,
	MarqueeSelectionCompat as MarqueeSelection,
	SelectionMode,
	useTheme,
} from 'components/FluentUICompat';
import {groupBy, mapValues} from 'helpers';
import {useCommand} from 'hooks';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {EmptyResults} from './EmptyResults';
import {useUserContext} from 'authentication/UserContext';
import {Column} from 'types';
import {
	ProviderThatEnablesGettingTooltipsFromContext,
	ColumnWithTooltipOptions,
} from 'features/localizedTooltips';
import {getRendererForHeaderWithTooltips} from '../../features/localizedTooltips/componentsWithTooltips/HeaderWithTooltipsAndStickySupport';
import _ from 'lodash';
import {isReportsTable} from './EntityUtils';
import {useEntityListContext} from './EntityListContext';
import {ExcelExportButton} from './Excel/ExcelExportButton';
import {excelButtonHide} from './Excel/ExcelExportCommon.react';
import {FaqPanel} from '../Faq/FaqPanel';
import {useEntityContext} from 'components/EntityPage/EntityContext';
import {v4 as uuidv4} from 'uuid';
import {EntityListColumnConfigPanel} from './EntityListColumnConfig';
import {EntityListFilterPanel} from './EntityListFilter';
import {useTranslation} from 'react-i18next';
import {useBoolean} from '@fluentui/react-hooks';
import {EntityListFilterBubbles} from './EntityListFilterBubbles';
import {IQuestionEntity} from 'components/Faq/CreateQuestionDialog';
import {GridView, IGridView} from './GridView/GridView';

export type EntityListColumn = ColumnWithTooltipOptions & {
	sortable?: boolean;
	filterable?: boolean;
	primitiveFilter?: boolean;
	hidden?: boolean;
	getFilterLabel?: (item: any) => string;
	getFilterKey?: (item: any) => string | number;
	getSortKey?: (item: any) => string | number;
	getFieldValue?: (item: any) => any;
	getTranformMatrix?: (item: any) => any[];
	fnFilterOptionsSort?: (a: string, b: string) => number;
	tableId?: string;
	filterOnFilter?: boolean;
	dataType?: string;
};

export type EntityListProps = Omit<IDetailsListProps, 'columns'> & {
	columns: EntityListColumn[];
	tableId?: string;
	fixedHeight?: string | number;
	listId?: string;
	sticky?: boolean;
	groupByFieldName?: string;
	enhanceColumnVisibilityButton?: boolean;
	questionItem?: IQuestionEntity;
	parentId?: string;
	hideExcelButton?: boolean;
	hideItemNumber?: boolean;
	hideFilterOptions?: boolean;
	viewGrid?: boolean;
	propsGridView?: IGridView;
};

export type EntityListFilter = {
	columnKey: string;
	selection: string;
	display: string;
	filterFn: (row: any) => boolean;
};

export const EntityListContext = React.createContext<{
	currentColumnConfig: Column[];
	setCurrentColumnConfig: React.Dispatch<React.SetStateAction<Column[]>>;
}>({} as any);

/**
 * Note: Tooltips are enabled by default, so you must wrap this component in a
 * tooltips translation provider and provide tooltips for all columns. See
 * TooltipTranslationContext for more info.
 *
 * You can also disable the tooltip using the column's tooltip options. See the
 * column's type definition for more info.
 */
export const EntityList: React.FC<EntityListProps> = props => {
	const sortedItemsRef = useRef<any[]>([]);
	const [sortedItems, setSortedItems] = useState<any[]>([]);
	sortedItemsRef.current = sortedItems;
	const [appliedFilters, setAppliedFilters] = useState<EntityListFilter[]>([]);

	const [selectedFilters, setSelectedFilters] = useState<EntityListFilter[]>(
		[],
	);

	const [currentColumnConfig, setCurrentColumnConfig] = useState<Column[]>([]);

	const {
		isAdmin,
		isVko,
		isVex,
		isReader,
		isBedarfskoordinator,
		myVexClusters,
		tableConfigurations,
		loadTableConfigurations,
	} = useUserContext();

	const {pageDetails} = useEntityListContext();

	const hasLoadedTableConfigurations = useRef(false);
	useEffect(() => {
		if (!hasLoadedTableConfigurations.current) {
			hasLoadedTableConfigurations.current = true;
			loadTableConfigurations();
		}
	}, [loadTableConfigurations]);

	const columnConfigs = useMemo(
		() =>
			tableConfigurations?.find(tc => tc.tableId === props.tableId)?.columns,
		[tableConfigurations],
	);

	const {setSelectedItems} = useEntityContext<any>();

	// update the selected items inside the context with the item user selected on the screen
	useEffect(() => {
		if (props?.selection && setSelectedItems) {
			setSelectedItems((props?.selection as any)?._selectedItems || []);
		}
	}, [(props?.selection as any)?._selectedItems, setSelectedItems]);

	// update the visibility and width of columns, if the user changes the config
	useEffect(() => {
		const newColumnConfig: Column[] = props.columns.map(c => {
			const userColumnIndex =
				columnConfigs?.findIndex(uc => uc.key === c.key) || -1;

			// Apply existing user config column if possible
			if (columnConfigs && userColumnIndex > -1) {
				return {
					hidden: columnConfigs[userColumnIndex].hidden,
					key: columnConfigs[userColumnIndex].key,
					width: columnConfigs[userColumnIndex].width,
				};
			}
			return {
				hidden: false,
				width: c.minWidth,
				key: c.key,
			};
		});

		setCurrentColumnConfig(newColumnConfig);
		setColumns(cols =>
			cols.map(c => {
				const config = newColumnConfig.find(cfg => cfg.key === c.key);

				if (!config) {
					return {...c};
				}

				return {
					...c,
					hidden: config.hidden,
					minWidth: config.width,
				};
			}),
		);
	}, [columnConfigs]);

	// custom function for handling the column resize event
	const onColumnResize = useCallback((col?: IColumn, newWidth?: number) => {
		if (!(col && newWidth)) {
			return;
		}

		setCurrentColumnConfig(cfg => {
			const newConfig = cfg.slice();
			const i = newConfig.findIndex(c => c.key === col?.key);
			const changedCol = newConfig[i];
			if (i !== -1) {
				newConfig[i] = {
					...changedCol,
					width: newWidth,
				};
			}

			return newConfig;
		});
	}, []);

	const baseColumnsRef = useRef<Array<EntityListColumn>>([]);

	const onColumnClick = useCallback(
		(_event: React.MouseEvent<HTMLElement>, column: IColumn): void => {
			let {isSortedDescending} = column;

			// If we've sorted this column, flip it.
			if (column.isSorted) {
				isSortedDescending = !isSortedDescending;
			}

			if ((column as any).customSorting) {
				setSortedItems(
					(column as any).customSorting(
						sortedItemsRef.current,
						column,
						isSortedDescending,
					),
				);
			} else {
				setSortedItems(
					_copyAndSort(sortedItemsRef.current, column, isSortedDescending),
				);
			}
			setColumns(
				baseColumnsRef.current.map(col => {
					col.isSorted = col.key === column.key;

					if (col.isSorted) {
						col.isSortedDescending = isSortedDescending;
					}

					return col;
				}),
			);
		},
		[],
	);

	const convertColumns = (props: EntityListProps) => {
		return props.columns.reduce((cols, column) => {
			const columnCopy = {...column};

			return cols.concat(
				// Note: onColumnClick is a stable useCallback reference that reads
				// sortedItems and baseColumns via refs, avoiding stale closure issues.
				column.sortable
					? {
							...columnCopy,
							sortAscendingAriaLabel: 'Sorted A to Z',
							sortDescendingAriaLabel: 'Sorted Z to A',
							filterAriaLabel: 'Filter active',
							showSortIconWhenUnsorted: true,
							onColumnClick,
					  }
					: columnCopy,
			);
		}, [] as Array<EntityListColumn>);
	};

	const [baseColumns, setColumns] = useState<Array<EntityListColumn>>(
		convertColumns(props),
	);

	useEffect(() => {
		setColumns(convertColumns(props));
	}, [props.columns]);

	baseColumnsRef.current = baseColumns;

	const filterColumns = (): EntityListColumn[] => {
		return baseColumns.filter(c => !c.hidden);
	};

	const columns: EntityListColumn[] = useMemo(filterColumns, [baseColumns]);

	const setInitialSortedItems = useCallback((): void => {
		const getInitialSortedItems = (): EntityListProps['items'] => {
			type Match = EntityListColumn | undefined;
			const sortedColumn: Match = _.find(columns, {isSorted: true});

			const itemsLocal = (props.items ?? []).map(
				x => props?.propsGridView?.dsChangedItems?.[x.id] || x,
			);
			return _copyAndSort(
				itemsLocal,
				sortedColumn,
				sortedColumn?.isSortedDescending,
			);
		};

		const newSortedItems: EntityListProps['items'] = getInitialSortedItems();
		setSortedItems(newSortedItems);
	}, [props.items, props?.propsGridView?.dsChangedItems, columns]);

	/**
	 * We only trigger this hook when the list of items changes and not when the
	 * function changes because it has "columns" as a dependency. If a component
	 * forgot to memoize the columns, then it would cause this function to fire
	 * unnecessarily, which would cause UX problems.
	 */
	useEffect(setInitialSortedItems, [
		props.items,
		props?.propsGridView?.dsChangedItems,
	]);

	const sortedFilteredItems = useMemo(
		() => getSortedFilteredItems(sortedItems, appliedFilters, baseColumns),
		[sortedItems, appliedFilters],
	);

	const {groups, finalItems} = useMemo(() => {
		let finalItems = sortedFilteredItems;

		if (props.groupByFieldName) {
			const groups: IGroup[] = [];

			const itemsByGroup = mapValues(
				groupBy(sortedFilteredItems, props.groupByFieldName),
				y => y.slice(0),
			);

			finalItems = [];

			for (const [groupKey] of Object.entries(itemsByGroup)) {
				const groupItems = itemsByGroup[groupKey];

				groups.push({
					key: groupKey,
					name: groupKey,
					isCollapsed: false,
					startIndex: finalItems.length,
					count: groupItems.length,
				});

				finalItems = [...finalItems, ...(groupItems as any[])];
			}

			return {groups, finalItems};
		}

		return {groups: undefined, finalItems};
	}, [sortedFilteredItems]);

	const entityContextValue = useMemo(
		() => ({
			currentColumnConfig,
			setCurrentColumnConfig,
		}),
		[currentColumnConfig, setCurrentColumnConfig],
	);

	const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> =
		getRendererForHeaderWithTooltips(props.sticky);

	const boolExcelButtonHide = useMemo(
		() =>
			props.hideExcelButton === true ||
			excelButtonHide(finalItems || []) ||
			props.viewGrid === true,
		[finalItems, props.viewGrid],
	);

	// The listRef helps to scroll to top when conditions are met
	const listRef = useRef<IDetailsList>(undefined);

	// Memorized header of list with views, filter bubbles and filter panel as well each filter item
	const compHeader = useMemo(
		() =>
			baseColumns?.some(col => col.filterable) && (
				<EntityListHeader
					columns={columns}
					finalItems={finalItems}
					appliedFilters={appliedFilters}
					selectedFilters={selectedFilters}
					listRef={listRef}
					setAppliedFilters={setAppliedFilters}
					setSelectedFilters={setSelectedFilters}
				/>
			),
		[
			baseColumns,
			columns,
			appliedFilters,
			selectedFilters,
			finalItems,
			listRef,
		],
	);

	const showGridView = props.viewGrid === true;

	useCommand(
		{
			key: 'excelExport_' + uuidv4(),
			farCommand: true,
			priority: 0,
			hidden: boolExcelButtonHide,
			commandBarButtonAs(): React.ReactElement {
				return (
					<ExcelExportButton
						entityListItems={finalItems}
						arrRoles={{isAdmin, isVko, isVex, isReader, isBedarfskoordinator}}
						myVexClusters={myVexClusters}
						pageDetails={pageDetails}
						columns={columns}
					/>
				);
			},
		},
		[
			appliedFilters,
			finalItems,
			myVexClusters,
			pageDetails,
			columns,
			boolExcelButtonHide,
		],
	);

	return (
		<EntityListContext.Provider value={entityContextValue}>
			{props.hideItemNumber !== true && (
				<EntityListNumberElementsCommand numberOfElements={finalItems.length} />
			)}
			{compHeader}
			{showGridView ? (
				<GridView
					itemsFiltered={finalItems}
					entityListColumns={columns}
					appliedFilters={appliedFilters}
					{...props.propsGridView!}
				/>
			) : (
				<MarqueeSelection
					selection={props.selection as ISelection}
					isDraggingConstrainedToRoot={true}
					isEnabled={props.selectionMode === SelectionMode.multiple}
				>
					<ProviderThatEnablesGettingTooltipsFromContext>
						<DetailsList
							{...props}
							checkboxVisibility={
								props.checkboxVisibility ??
								props.selectionMode === SelectionMode.multiple
									? CheckboxVisibility.always
									: CheckboxVisibility.onHover
							}
							styles={
								props.fixedHeight
									? getFixedHeightListStyles(props.fixedHeight)
									: props.styles
							}
							items={finalItems}
							groups={groups}
							columns={columns}
							onRenderDetailsHeader={onRenderDetailsHeader}
							selectionPreservedOnEmptyClick={true}
							constrainMode={props.constrainMode || ConstrainMode.unconstrained}
							onColumnResize={onColumnResize}
						/>
					</ProviderThatEnablesGettingTooltipsFromContext>
					{sortedFilteredItems.length === 0 && <EmptyResults />}
				</MarqueeSelection>
			)}
			<FaqPanel parentId={props.parentId} />
			<EntityListColumnConfigPanel
				currentColumnConfig={currentColumnConfig}
				{...props}
			/>
		</EntityListContext.Provider>
	);
};

function _copyAndSort<T>(
	items: T[],
	column?: EntityListColumn,
	isSortedDescending?: boolean,
): T[] {
	if (!column) {
		return items.slice(0);
	}

	const convertArrayEntity = (val: any) => {
		if (Array.isArray(val)) {
			switch (val[0]?.__typename) {
				case 'VexCluster':
				case 'User':
				case 'RegulationCluster':
					return val[0].name;
				default:
					return val[0] || '';
			}
		}

		return val;
	};

	const keys = (column.fieldName || '').split('.');
	const {getSortKey} = column;
	return items.slice(0).sort((a: T, b: T) => {
		let aVal: any;
		let bVal: any;
		keys.forEach(key => {
			aVal = aVal ? aVal[key] : a[key as keyof T];
			bVal = bVal ? bVal[key] : b[key as keyof T];
		});
		aVal = convertArrayEntity(aVal);
		bVal = convertArrayEntity(bVal);
		aVal = getSortKey ? getSortKey(aVal) : aVal;
		bVal = getSortKey ? getSortKey(bVal) : bVal;
		aVal = typeof aVal === 'string' ? aVal.toLowerCase() : aVal;
		bVal = typeof bVal === 'string' ? bVal.toLowerCase() : bVal;

		const toggleKey = isSortedDescending ? aVal < bVal : aVal > bVal;
		return toggleKey ? 1 : -1;
	});
}

const getFixedHeightListStyles: (
	height: string | number,
) => Partial<IDetailsListStyles> = height => ({
	root: {
		overflowX: 'scroll',
		selectors: {
			'& [role=grid]': {
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'start',
				height,
			},
		},
	},
	headerWrapper: {
		flex: '0 0 auto',
	},
	contentWrapper: {
		flex: '1 1 auto',
		overflowY: 'auto',
		overflowX: 'hidden',
	},
});

type EntityListNumberElementsCommandProps = {
	numberOfElements: number;
	itemName?: string;
};

const EntityListNumberElementsCommand: React.FC<
	EntityListNumberElementsCommandProps
> = ({numberOfElements, itemName}) => {
	const {t} = useTranslation('components/infinitylist');
	const theme = useTheme();

	useCommand(
		{
			key: uuidv4(),
			farCommand: true,
			priority: 1000,
			commandBarButtonAs(): React.ReactElement {
				return (
					<div
						style={{
							paddingTop: 12,
							paddingLeft: 20,
							background: theme.palette.neutralLight,
							width: 105,
						}}
					>
						{`${numberOfElements} ${itemName ?? t('Elements')}`}
					</div>
				);
			},
		},
		[numberOfElements],
	);
	return <></>;
};

type EntityListHeaderProps = {
	columns: EntityListColumn[];
	finalItems: any[];
	appliedFilters: EntityListFilter[];
	selectedFilters: EntityListFilter[];
	setAppliedFilters: (selectedFilters: EntityListFilter[]) => void;
	setSelectedFilters: (selectedFilters: EntityListFilter[]) => void;
	listRef: React.MutableRefObject<IDetailsList | undefined>;
};

const EntityListHeader: React.FC<EntityListHeaderProps> = ({...props}) => {
	const [isPanelOpened, {setTrue: openPanel, setFalse: dismissPanel}] =
		useBoolean(false);

	return (
		<div
			style={{
				marginLeft: 20,
				marginTop: props.appliedFilters.length > 0 ? 20 : 0,
			}}
		>
			<EntityListFilterBubbles {...props} />
			<EntityListFilterPanel
				isPanelOpened={isPanelOpened}
				openPanel={openPanel}
				dismissPanel={dismissPanel}
				{...props}
			/>
		</div>
	);
};

export const getSortedFilteredItems = (
	sortedItems: any[],
	appliedFilters: EntityListFilter[],
	baseColumns: EntityListColumn[],
) => {
	if (!appliedFilters.length) {
		return sortedItems;
	}

	return sortedItems.filter(item => {
		const filterableColumns = baseColumns.filter(col => col.filterable);

		const filterRes: boolean[] = filterableColumns.map(col => {
			const colFilters = appliedFilters.filter(f => f.columnKey === col.key);

			if (!colFilters.length) {
				return true;
			}

			for (const filter of colFilters) {
				if (filter.filterFn(item)) {
					return true;
				}
			}

			return false;
		});

		return filterRes.reduce((res, curr) => res && curr, true);
	});
};

export const ROW_CLASS = 'ms-DetailsRow';
export const ROW_SELECTOR = `.${ROW_CLASS}`;
export const HEIGHT_OF_LIST_HEADER = 60;
